/*
  WyshineCrochet Smart Search Suggestion - Optimized Version
  Version: 2026-05-06-v2

  Main improvements in this version:
  - Large keyword list generated from current Etsy listings.
  - Token-based spelling correction for multi-word queries.
  - Debounce while typing to reduce mobile CPU usage.
  - Bounded Levenshtein with early-exit.
  - Small LRU-style cache for repeated query checks.
  - Keyword index by word length and first letter.
  - Google/Etsy-style auto-correct on submit when confidence is high:
      /search?q=spider%20crochet&original_q=spidar%20crochat&corrected=1
  - Search results page note:
      Showing results for “spider crochet”
      Search instead for “spidar crochat”
  - Safer recent-search rendering without injecting user text into innerHTML.
*/

const WYSHINE_KEYWORDS_RAW = [
  // Core search / product format
  'wyshine',
  'wyshinecrochet',
  'crochet',
  'pattern',
  'patterns',
  'amigurumi',
  'plushie',
  'plush',
  'pdf',
  'tutorial',
  'guide',
  'digital',
  'download',
  'bundle',
  'set',
  'family',
  'couple',
  'trio',
  'ornament',
  'ornaments',
  'keychain',
  'hanger',
  'car hanger',
  'scarf',
  'snuggler',
  'pillow',
  'stuffed',
  'toy',
  'decor',
  'decoration',
  'gift',
  'gifts',
  'home decor',
  'market prep',
  'crochet market prep',
  'beginner',
  'easy',
  'quick',
  'fast',
  'no sew',
  'low sew',
  'huggable',
  'chubby',
  'realistic',
  'chenille',
  'bulky yarn',
  'yarn',

  // Seasonal / occasion
  'halloween',
  'spooky',
  'creepy',
  'creepy cute',
  'gothic',
  'fall',
  'autumn',
  'thanksgiving',
  'christmas',
  'xmas',
  'holiday',
  'winter',
  'valentine',
  'valentines',
  'easter',
  'spring',
  'summer',
  'mother day',
  'mothers day',
  'father day',
  'fathers day',
  'birthday',
  'graduation',
  'wedding',
  'anniversary',

  // Animals and characters
  'turtle',
  'sea turtle',
  'dragon',
  'baby dragon',
  'dinosaur',
  'brontosaurus',
  'triceratops',
  'dino',
  'spider',
  'bat',
  'pumpkin',
  'cat',
  'kitten',
  'kitty',
  'calico',
  'bunny',
  'rabbit',
  'chicken',
  'hen',
  'rooster',
  'duck',
  'duckling',
  'mallard',
  'octopus',
  'cow',
  'highland cow',
  'calf',
  'reindeer',
  'deer',
  'rudolph',
  'snowman',
  'snowgirl',
  'snowwoman',
  'santa',
  'santa claus',
  'elf',
  'gingerbread',
  'gingerbread man',
  'gingerbread girl',
  'monster',
  'lion',
  'fox',
  'moose',

  // Fruit / flower / food themes
  'fruit',
  'strawberry',
  'kiwi',
  'orange',
  'dragonfruit',
  'dragon fruit',
  'watermelon',
  'candy',
  'candy cane',
  'gummy',
  'marshmallow',
  'mocha',
  'chocolate',
  'chestnut',
  'carrot',
  'rose',
  'sunflower',
  'daisy',
  'flower',
  'pine',
  'pine tree',
  'tree',
  'snowflake',
  'snow flake',
  'star',
  'heart',

  // Turtle products
  'sunflower turtle',
  'rose turtle',
  'flower turtle',
  'sea turtle crochet',
  'turtle crochet',
  'turtle amigurumi',
  'turtle pattern',
  'turtle plush',
  'baby turtle',
  'mom turtle',
  'mom and baby turtle',
  'mom baby turtle',
  'mom and baby rose turtle',
  'rose turtle bundle',
  'sunflower sea turtle',
  'flower sea turtle',

  // Dragon products
  'dragon crochet',
  'crochet dragon',
  'dragon amigurumi',
  'dragon pattern',
  'baby dragon crochet',
  'baby dragon pattern',
  'tree dragon',
  'christmas dragon',
  'christmas tree dragon',
  'xmas tree dragon',
  'gingerbread dragon',
  'candy dragon',
  'candy cane dragon',
  'cupid dragon',
  'little cupid dragon',
  'forest dragon',
  'playful dragon',
  'mini dragon',
  'sitting dragon',
  'seating dragon',

  // Spider and Halloween products
  'spider crochet',
  'crochet spider',
  'spider pattern',
  'spider crochet pattern',
  'fruit spider',
  'fruit spider pattern',
  'flower spider',
  'flower spiders',
  'strawberry spider',
  'kiwi spider',
  'orange spider',
  'dragonfruit spider',
  'dragon fruit spider',
  'pumpkin spider',
  'rose spider',
  'sunflower spider',
  'daisy spider',
  'forest spider',
  'halloween spider',
  'halloween spiders',
  'halloween amigurumi',
  'halloween crochet',
  'pumpkin crochet',
  'crochet pumpkin',
  'pumpkin pattern',
  'pumpkin plushie',
  'pumpkin amigurumi',
  'pumpkin decor',
  'pumpkin home decor',
  'pumpkin spice',
  'fall pumpkin',

  // Chicken / farm products
  'chicken crochet',
  'crochet chicken',
  'chicken amigurumi',
  'fruit chicken',
  'fruit chicken plush',
  'strawberry chicken',
  'strawberry rooster',
  'strawberry hen',
  'kiwi chicken',
  'kiwi hen',
  'kiwi rooster',
  'green kiwi rooster',
  'golden kiwi hen',
  'hen rooster',
  'farm animal',
  'farm animal crochet',
  'farmhouse decor',
  'bird crochet',
  'bird pattern',

  // Cat products
  'cat crochet',
  'cat crochet pattern',
  'crochet cat',
  'cat amigurumi',
  'calico cat',
  'calico cat amigurumi',
  'sleeping cat',
  'sleeping calico cat',
  'fat cat',
  'chubby cat',
  'kitten crochet',
  'kitty crochet',
  'kitten pattern',
  'kitty pattern',
  'cat couple',
  'kitten family',
  'no sew cat',
  'cat plushie',
  'cat pillow',
  'cat lover gift',
  'pet crochet',

  // Dinosaur products
  'dinosaur crochet',
  'crochet dinosaur',
  'dinosaur amigurumi',
  'dino crochet',
  'dino pattern',
  'brontosaurus crochet',
  'brontosaurus dinosaur',
  'cowboy brontosaurus',
  'cowboy dinosaur',
  'couple brontosaurus',
  'mommy baby brontosaurus',
  'mom baby dinosaur',
  'flower brontosaurus',
  'flower dino',
  'flower dino crochet',
  'triceratops crochet',
  'triceratops dinosaur',
  'triceratops pattern',
  'tricerabloom',
  'bronty',

  // Christmas / ornament products
  'christmas crochet',
  'crochet christmas',
  'christmas pattern',
  'christmas amigurumi',
  'christmas ornament',
  'christmas tree ornament',
  'tree ornament',
  'crochet ornament',
  'ornament crochet',
  'holiday crochet',
  'holiday decor',
  'holiday home decor',
  'holiday decoration',
  'xmas ornament',
  'xmas toy',
  'xmas toy decoration',
  'christmas tree',
  'crochet tree',
  'tree crochet pattern',
  'layered tree',
  'ruffled tree',
  'pinetree',
  'pine tree star',
  'santa crochet',
  'santa claus crochet',
  'elf crochet',
  'green elf',
  'green monster',
  'snowman crochet',
  'crochet snowman',
  'amigurumi snowman',
  'chubby snowman',
  'santa snowman',
  'snowgirl crochet',
  'candy snowgirl',
  'snowman snowgirl',
  'trio snowman',
  'gingerbread crochet',
  'gingerbread couple',
  'gingerbread cow',
  'gingerbread highland cow',
  'gingerbread woman',
  'gingerbread women',

  // Reindeer / winter products
  'reindeer crochet',
  'crochet reindeer',
  'reindeer amigurumi',
  'baby reindeer',
  'mom baby reindeer',
  'mommy baby reindeer',
  'family reindeer',
  'couple reindeer',
  'chestnut reindeer',
  'flower reindeer',
  'rudolph crochet',
  'rudolph plush',
  'reindeer scarf',
  'winter crochet',
  'winter amigurumi',
  'winter decor',

  // Cow products
  'cow crochet',
  'cow crochet pattern',
  'crochet cow',
  'cow amigurumi',
  'highland cow crochet',
  'candy cane cow',
  'sweet cow',
  'snuggler cow',
  'snuggler cow crochet',
  'calf crochet',
  'calf amigurumi',
  'baby snuggle animal',

  // Bunny / Easter products
  'bunny crochet',
  'crochet bunny',
  'bunny amigurumi',
  'bunny pattern',
  'rabbit crochet',
  'crochet rabbit',
  'rabbit amigurumi',
  'realistic rabbit',
  'realistic bunny',
  'mocha bunny',
  'carrot bunny',
  'brown bunny',
  'gummy candy bunny',
  'marshmallow bunny',
  'sitting bunny',
  'laying bunny',
  'sitting rabbit',
  'laying rabbit',
  'no sew bunny',
  'peep bunny',
  'easter bunny',
  'easter crochet',
  'easter decor',

  // Sea animal / other animals
  'kiwi octopus',
  'octopus crochet',
  'crochet octopus',
  'amigurumi octopus',
  'sea animal crochet',
  'sea animals',
  'ocean toy',
  'beach decor',
  'rubber duck',
  'duck crochet',
  'crochet duck',
  'duck couple',
  'duck amigurumi',
  'duckling stuffed',
  'mallard crochet',
  'fox crochet',
  'crochet fox',
  'fox amigurumi',
  'fox plush',
  'forest animal',
  'woodland animal',
  'lion crochet',
  'crochet lion',
  'lion amigurumi',
  'safari animal',
  'wild animals',
  'wild animals crochet',

  // Special product names / brand memory
  'mathilda',
  'anchor',
  'wyshine crochet',
  'wyshine crochet pattern',
];

const WYSHINE_SEARCH_CONFIG = {
  recentStorageKey: 'wyshine_recent_searches',
  maxRecentSearches: 5,
  minWordLength: 3,
  maxSuggestionPhrases: 6,
  debounceDelay: 260,
  cacheLimit: 180,
  autoCorrectOnSubmit: true,

  selectors: {
    predictiveSearch: 'predictive-search',
    predictiveWrapper: '.wyshine-predictive-wrapper',
    searchInput: 'input[type="search"], input[name="q"]',
    searchInputsToFocus: '.etsy-search-input, .desktop-search-input, input[type="search"], input[name="q"]',
    mobileHeader: '#mobile-header',
    headerWrapper: '.header-wrapper',
    searchResults: '.wyshine-search-results',
    realResultItem: 'a.predictive-search__item',
    zeroState: '.wyshine-zero-state-container',
    suggestionBox: '.wyshine-suggestion-box',
    suggestionLink: '.wyshine-suggestion-link',
    clearButton: '.wyshine-search-clear',
    recentBlock: '.wyshine-recent-searches-block',
    recentList: '.wyshine-recent-list',
    clearAllRecent: '.wyshine-clear-all-btn',
    searchCancelButton: '.search-cancel-btn',
    trendingSourceChips: '.trending-bar-section .trending-chip',
    trendingTargetBlocks: '.wyshine-trending-block-dynamic',
    trendingTargetContainer: '.wyshine-dynamic-trending-container',
    mainContent: '#MainContent, main, .main-content, body'
  }
};

const WYSHINE_COMMON_TYPOS = {
  crohet: 'crochet',
  crochat: 'crochet',
  corchet: 'crochet',
  crotchet: 'crochet',
  croche: 'crochet',
  crochetet: 'crochet',
  patern: 'pattern',
  patten: 'pattern',
  patttern: 'pattern',
  paterns: 'patterns',
  amigrumi: 'amigurumi',
  amigumi: 'amigurumi',
  amigurmi: 'amigurumi',
  amigurumi: 'amigurumi',
  plushy: 'plushie',
  plusie: 'plushie',
  plushis: 'plushie',
  plushyie: 'plushie',
  haloween: 'halloween',
  hallowen: 'halloween',
  halloween: 'halloween',
  christmass: 'christmas',
  chrismas: 'christmas',
  chirstmas: 'christmas',
  xmass: 'xmas',
  strawbery: 'strawberry',
  strawberrry: 'strawberry',
  stawberry: 'strawberry',
  dragonfriut: 'dragonfruit',
  dragonfrut: 'dragonfruit',
  dragonfuit: 'dragonfruit',
  pumpin: 'pumpkin',
  pumkin: 'pumpkin',
  pumpkn: 'pumpkin',
  turtel: 'turtle',
  turttle: 'turtle',
  tutrle: 'turtle',
  spidar: 'spider',
  spidr: 'spider',
  spder: 'spider',
  bunnie: 'bunny',
  buny: 'bunny',
  rabit: 'rabbit',
  dinosour: 'dinosaur',
  dinasaur: 'dinosaur',
  octupus: 'octopus',
  slepping: 'sleeping',
  sleppy: 'sleepy',
  orniment: 'ornament',
  orniments: 'ornaments',
  chenile: 'chenille',
  chennile: 'chenille',
  gingebread: 'gingerbread',
  gingerbread: 'gingerbread'
};

function normalizeWyshineSearchText(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function displayWyshineText(text) {
  const normalized = normalizeWyshineSearchText(text);
  return normalized.replace(/\b\w/g, char => char.toUpperCase());
}

function dedupeStrings(items) {
  const seen = new Set();
  const result = [];

  items.forEach(item => {
    const clean = normalizeWyshineSearchText(item);
    if (!clean || seen.has(clean)) return;
    seen.add(clean);
    result.push(clean);
  });

  return result;
}

function debounceWyshine(fn, delay) {
  let timerId = null;

  return function debouncedFunction(...args) {
    window.clearTimeout(timerId);
    timerId = window.setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function maxDistanceForWord(word) {
  if (word.length <= 4) return 1;
  if (word.length <= 8) return 2;
  return 3;
}

function getBoundedLevenshteinDistance(a, b, maxDistance) {
  if (a === b) return 0;

  const aLength = a.length;
  const bLength = b.length;

  if (Math.abs(aLength - bLength) > maxDistance) return Infinity;
  if (aLength === 0) return bLength <= maxDistance ? bLength : Infinity;
  if (bLength === 0) return aLength <= maxDistance ? aLength : Infinity;

  let previous = new Array(bLength + 1);
  let current = new Array(bLength + 1);

  for (let j = 0; j <= bLength; j++) {
    previous[j] = j;
  }

  for (let i = 1; i <= aLength; i++) {
    current[0] = i;
    let rowMin = current[0];

    for (let j = 1; j <= bLength; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      const deletion = previous[j] + 1;
      const insertion = current[j - 1] + 1;
      const substitution = previous[j - 1] + cost;
      const value = Math.min(deletion, insertion, substitution);

      current[j] = value;
      if (value < rowMin) rowMin = value;
    }

    // Early exit: if the best value in this row is already above the threshold,
    // the final distance cannot become useful for our spelling correction.
    if (rowMin > maxDistance) return Infinity;

    const temp = previous;
    previous = current;
    current = temp;
  }

  return previous[bLength] <= maxDistance ? previous[bLength] : Infinity;
}

const WYSHINE_KEYWORD_INDEX = (() => {
  const rawKeywords = dedupeStrings(WYSHINE_KEYWORDS_RAW);
  const singleWords = new Set();
  const phraseSet = new Set();
  const phrases = [];
  const wordsByLength = new Map();
  const wordsByLengthAndFirst = new Map();

  function pushUniqueToMap(map, key, value) {
    if (!map.has(key)) map.set(key, []);
    const bucket = map.get(key);
    if (!bucket.includes(value)) bucket.push(value);
  }

  function addWord(word) {
    const cleanWord = normalizeWyshineSearchText(word);
    if (!cleanWord || cleanWord.length < WYSHINE_SEARCH_CONFIG.minWordLength) return;

    singleWords.add(cleanWord);

    const length = cleanWord.length;
    const first = cleanWord.charAt(0);

    pushUniqueToMap(wordsByLength, String(length), cleanWord);
    pushUniqueToMap(wordsByLengthAndFirst, `${length}:${first}`, cleanWord);
  }

  rawKeywords.forEach(term => {
    if (term.includes(' ')) {
      if (!phraseSet.has(term)) {
        phraseSet.add(term);
        phrases.push(term);
      }
      term.split(' ').forEach(addWord);
    } else {
      addWord(term);
    }
  });

  phrases.sort((a, b) => b.length - a.length);

  return {
    rawKeywords,
    singleWords,
    phraseSet,
    phrases,
    wordsByLength,
    wordsByLengthAndFirst
  };
})();

const WYSHINE_SUGGESTION_CACHE = new Map();

function getFromWyshineCache(key) {
  if (!WYSHINE_SUGGESTION_CACHE.has(key)) return undefined;
  const value = WYSHINE_SUGGESTION_CACHE.get(key);
  WYSHINE_SUGGESTION_CACHE.delete(key);
  WYSHINE_SUGGESTION_CACHE.set(key, value);
  return value;
}

function setWyshineCache(key, value) {
  WYSHINE_SUGGESTION_CACHE.set(key, value);

  while (WYSHINE_SUGGESTION_CACHE.size > WYSHINE_SEARCH_CONFIG.cacheLimit) {
    const oldestKey = WYSHINE_SUGGESTION_CACHE.keys().next().value;
    WYSHINE_SUGGESTION_CACHE.delete(oldestKey);
  }
}

function singularFallback(word) {
  if (WYSHINE_KEYWORD_INDEX.singleWords.has(word)) return word;

  if (word.endsWith('ies')) {
    const singularY = word.slice(0, -3) + 'y';
    if (WYSHINE_KEYWORD_INDEX.singleWords.has(singularY)) return singularY;
  }

  if (word.endsWith('es')) {
    const singularEs = word.slice(0, -2);
    if (WYSHINE_KEYWORD_INDEX.singleWords.has(singularEs)) return singularEs;
  }

  if (word.endsWith('s')) {
    const singular = word.slice(0, -1);
    if (WYSHINE_KEYWORD_INDEX.singleWords.has(singular)) return singular;
  }

  return null;
}

function getCandidateWords(word, maxDistance, useFirstLetterFilter) {
  const candidates = [];
  const seen = new Set();
  const first = word.charAt(0);

  function addBucket(bucket) {
    if (!bucket) return;
    bucket.forEach(candidate => {
      if (seen.has(candidate)) return;
      seen.add(candidate);
      candidates.push(candidate);
    });
  }

  for (let len = word.length - maxDistance; len <= word.length + maxDistance; len++) {
    if (len < WYSHINE_SEARCH_CONFIG.minWordLength) continue;

    if (useFirstLetterFilter) {
      addBucket(WYSHINE_KEYWORD_INDEX.wordsByLengthAndFirst.get(`${len}:${first}`));
    } else {
      addBucket(WYSHINE_KEYWORD_INDEX.wordsByLength.get(String(len)));
    }
  }

  return candidates;
}

function correctSingleWord(word) {
  const cleanWord = normalizeWyshineSearchText(word);

  if (!cleanWord || cleanWord.length < WYSHINE_SEARCH_CONFIG.minWordLength) {
    return { word: cleanWord, changed: false, confidence: 'none', distance: 0 };
  }

  if (WYSHINE_COMMON_TYPOS[cleanWord]) {
    return {
      word: WYSHINE_COMMON_TYPOS[cleanWord],
      changed: WYSHINE_COMMON_TYPOS[cleanWord] !== cleanWord,
      confidence: 'high',
      distance: 1
    };
  }

  if (WYSHINE_KEYWORD_INDEX.singleWords.has(cleanWord)) {
    return { word: cleanWord, changed: false, confidence: 'exact', distance: 0 };
  }

  const singular = singularFallback(cleanWord);
  if (singular && singular !== cleanWord) {
    return { word: singular, changed: true, confidence: 'high', distance: 1 };
  }

  const maxDistance = maxDistanceForWord(cleanWord);
  let bestMatch = null;
  let bestDistance = Infinity;
  let secondBestDistance = Infinity;

  function scanCandidates(candidates) {
    candidates.forEach(keyword => {
      const distance = getBoundedLevenshteinDistance(cleanWord, keyword, maxDistance);
      if (distance === Infinity || distance === 0) return;

      const ratio = distance / Math.max(keyword.length, cleanWord.length);
      if (ratio > 0.35) return;

      if (distance < bestDistance) {
        secondBestDistance = bestDistance;
        bestDistance = distance;
        bestMatch = keyword;
      } else if (distance < secondBestDistance && keyword !== bestMatch) {
        secondBestDistance = distance;
      }
    });
  }

  // Pass 1: faster path, compare only candidates with similar length and same first letter.
  scanCandidates(getCandidateWords(cleanWord, maxDistance, true));

  // Pass 2 fallback: catches cases where the first letter was mistyped.
  // This still stays light because we only scan similar-length buckets.
  if (!bestMatch) {
    scanCandidates(getCandidateWords(cleanWord, maxDistance, false));
  }

  if (!bestMatch) {
    return { word: cleanWord, changed: false, confidence: 'none', distance: Infinity };
  }

  let confidence = 'medium';
  if (bestDistance === 1) confidence = 'high';
  if (bestDistance === 2 && cleanWord.length >= 7 && secondBestDistance > bestDistance) confidence = 'high';
  if (secondBestDistance === bestDistance) confidence = 'low';

  return {
    word: bestMatch,
    changed: bestMatch !== cleanWord,
    confidence,
    distance: bestDistance
  };
}

function scorePhraseSuggestion(input, phrase) {
  const inputWords = input.split(' ').filter(Boolean);
  const phraseWords = phrase.split(' ').filter(Boolean);

  if (!inputWords.length || !phraseWords.length) return 0;

  let score = 0;

  inputWords.forEach(inputWord => {
    let bestWordScore = 0;

    phraseWords.forEach(phraseWord => {
      if (phraseWord === inputWord) {
        bestWordScore = Math.max(bestWordScore, 10);
      } else if (phraseWord.startsWith(inputWord) && inputWord.length >= 3) {
        bestWordScore = Math.max(bestWordScore, 7);
      } else if (inputWord.startsWith(phraseWord) && phraseWord.length >= 3) {
        bestWordScore = Math.max(bestWordScore, 5);
      } else {
        const maxDistance = maxDistanceForWord(inputWord);
        const distance = getBoundedLevenshteinDistance(inputWord, phraseWord, maxDistance);
        if (distance !== Infinity && distance > 0 && distance <= maxDistance) {
          bestWordScore = Math.max(bestWordScore, 5 - distance);
        }
      }
    });

    score += bestWordScore;
  });

  const lengthPenalty = Math.max(0, phraseWords.length - inputWords.length) * 0.8;
  return score - lengthPenalty;
}

function buildPhraseSuggestions(input, correctedInput) {
  const cleanInput = normalizeWyshineSearchText(correctedInput || input);
  if (cleanInput.length < WYSHINE_SEARCH_CONFIG.minWordLength) return [];

  const suggestions = [];

  WYSHINE_KEYWORD_INDEX.phrases.forEach(phrase => {
    if (phrase === cleanInput) return;

    const score = scorePhraseSuggestion(cleanInput, phrase);
    if (score >= 6) {
      suggestions.push({ phrase, score });
    }
  });

  suggestions.sort((a, b) => b.score - a.score || a.phrase.length - b.phrase.length);

  return suggestions
    .slice(0, WYSHINE_SEARCH_CONFIG.maxSuggestionPhrases)
    .map(item => item.phrase);
}

function buildWyshineSearchSuggestion(userInput, options = {}) {
  const input = normalizeWyshineSearchText(userInput);
  if (input.length < WYSHINE_SEARCH_CONFIG.minWordLength) return null;

  const cacheKey = `v2:${input}`;
  const cached = getFromWyshineCache(cacheKey);
  if (cached !== undefined && options.skipCache !== true) return cached;

  const words = input.split(' ').filter(Boolean);
  const correctedParts = words.map(correctSingleWord);
  const correctedQuery = correctedParts.map(part => part.word).join(' ').trim();
  const changedParts = correctedParts.filter(part => part.changed);
  const changed = changedParts.length > 0 && correctedQuery !== input;

  let confidence = 'none';
  if (changed) {
    confidence = changedParts.every(part => part.confidence === 'high') ? 'high' :
      changedParts.some(part => part.confidence === 'low') ? 'low' : 'medium';
  }

  const allWordsKnown = words.every(word => WYSHINE_KEYWORD_INDEX.singleWords.has(word));
  const exactKnownQuery = WYSHINE_KEYWORD_INDEX.singleWords.has(input) || WYSHINE_KEYWORD_INDEX.phraseSet.has(input);

  if (!changed && (allWordsKnown || exactKnownQuery)) {
    setWyshineCache(cacheKey, null);
    return null;
  }

  const phraseSuggestions = buildPhraseSuggestions(input, changed ? correctedQuery : input)
    .filter(phrase => phrase !== correctedQuery && phrase !== input);

  if (!changed && phraseSuggestions.length === 0) {
    setWyshineCache(cacheKey, null);
    return null;
  }

  const result = {
    original: input,
    corrected: changed ? correctedQuery : null,
    best: changed ? correctedQuery : phraseSuggestions[0],
    confidence,
    phraseSuggestions,
    shouldAutoCorrect: changed && confidence === 'high'
  };

  setWyshineCache(cacheKey, result);
  return result;
}

function createSvgIcon(type) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  if (type === 'search') {
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '11');
    circle.setAttribute('cy', '11');
    circle.setAttribute('r', '8');
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '21');
    line.setAttribute('y1', '21');
    line.setAttribute('x2', '16.65');
    line.setAttribute('y2', '16.65');
    svg.append(circle, line);
  }

  if (type === 'x') {
    const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line1.setAttribute('x1', '18');
    line1.setAttribute('y1', '6');
    line1.setAttribute('x2', '6');
    line1.setAttribute('y2', '18');
    const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line2.setAttribute('x1', '6');
    line2.setAttribute('y1', '6');
    line2.setAttribute('x2', '18');
    line2.setAttribute('y2', '18');
    svg.append(line1, line2);
  }

  return svg;
}

function safeParseRecentSearches() {
  try {
    const value = localStorage.getItem(WYSHINE_SEARCH_CONFIG.recentStorageKey) || '[]';
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(item => String(item).trim()).filter(Boolean) : [];
  } catch (error) {
    return [];
  }
}

function buildSearchUrlFromForm(form, finalTerm, originalTerm, isCorrected) {
  const action = form ? (form.getAttribute('action') || '/search') : '/search';
  const url = new URL(action, window.location.origin);

  if (form) {
    try {
      const formData = new FormData(form);
      formData.forEach((value, key) => {
        if (!key || key === 'q' || key === 'original_q' || key === 'corrected') return;
        url.searchParams.set(key, value);
      });
    } catch (error) {}
  }

  url.searchParams.set('q', finalTerm);

  if (isCorrected && originalTerm && normalizeWyshineSearchText(originalTerm) !== normalizeWyshineSearchText(finalTerm)) {
    url.searchParams.set('original_q', originalTerm);
    url.searchParams.set('corrected', '1');
  } else {
    url.searchParams.delete('original_q');
    url.searchParams.delete('corrected');
  }

  return `${url.pathname}${url.search}`;
}

function navigateToWyshineSearch(term, originalTerm = '', isCorrected = false, form = null) {
  const finalTerm = String(term || '').trim();
  if (!finalTerm) return;
  window.location.href = buildSearchUrlFromForm(form, finalTerm, originalTerm, isCorrected);
}

function injectWyshineCorrectionNote() {
  const params = new URLSearchParams(window.location.search);
  const corrected = params.get('corrected');
  const originalQuery = params.get('original_q');

  // Nếu không có sửa lỗi thì không làm gì cả
  if (corrected !== '1' || !originalQuery) return;

  const displayOriginal = originalQuery.replace(/\+/g, ' ');
  if (!displayOriginal) return;
  
  // Tránh chèn trùng lặp
  if (document.querySelector('.wyshine-search-instead-note')) return;

  // Chỉ tạo 1 dòng "Search instead for" chuẩn Google (chữ xanh, in nghiêng)
  const noteHTML = `
    <div class="wyshine-search-instead-note" style="margin-top: 5px; margin-bottom: 20px; font-size: 16px; color: #202124 !important; font-weight: normal !important; text-align: left; background: transparent !important; border: none !important;">
      Search instead for <a href="/search?q=${encodeURIComponent(displayOriginal)}" style="color: #0c2e77ff !important; text-decoration: none !important; font-style: italic !important;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${displayOriginal}</a>
    </div>
  `;

  // TÌM TIÊU ĐỀ TRANG (Thường là thẻ h1) ĐỂ CHÈN XUỐNG DƯỚI
  const pageTitle = document.querySelector('h1');
  
  if (pageTitle) {
    // Chèn ngay sau thẻ h1
    pageTitle.insertAdjacentHTML('afterend', noteHTML);
    // Thu hẹp khoảng cách mặc định của h1 để 2 dòng trông gắn kết với nhau hơn
    pageTitle.style.marginBottom = '5px'; 
  } else {
    // Fallback: Nếu theme không dùng h1, chèn vào khối nội dung chính
    const target = document.querySelector('.template-search__header, .wyshine-search-results, #MainContent, main');
    if (target) {
      target.insertAdjacentHTML('afterbegin', noteHTML);
    }
  }
}

const WyshineSearch = {
  keywordIndex: WYSHINE_KEYWORD_INDEX,
  normalize: normalizeWyshineSearchText,
  getSuggestion: buildWyshineSearchSuggestion,

  checkSpelling: function(userInput) {
    const suggestion = buildWyshineSearchSuggestion(userInput);
    return suggestion && suggestion.corrected ? suggestion.corrected : null;
  },

  requestSuggestionUpdate: null,

  syncTrendingKeywords: function() {
    const trendingBarChips = document.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.trendingSourceChips);
    const trendingBlocks = document.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.trendingTargetBlocks);

    if (!trendingBarChips.length || !trendingBlocks.length) return;

    trendingBlocks.forEach(block => {
      const container = block.querySelector(WYSHINE_SEARCH_CONFIG.selectors.trendingTargetContainer);
      if (!container) return;

      container.replaceChildren();

      trendingBarChips.forEach(chip => {
        const term = chip.textContent.trim();
        const href = chip.getAttribute('href');
        if (!term || !href) return;

        const link = document.createElement('a');
        link.href = href;
        link.className = 'wyshine-trending-pill';
        link.textContent = term;
        link.addEventListener('pointerdown', function(event) {
          event.preventDefault();
          window.location.href = link.href;
        });
        container.appendChild(link);
      });

      block.style.display = container.children.length ? 'block' : 'none';
    });
  },

  submitSearchForm: function(form, term, originalTerm = '', isCorrected = false) {
    if (!form) return;

    const input = form.querySelector(WYSHINE_SEARCH_CONFIG.selectors.searchInput);
    const finalTerm = String(term || (input ? input.value : '') || '').trim();

    if (!finalTerm) return;

    if (input) input.value = finalTerm;
    this.saveRecentSearch(finalTerm);

    navigateToWyshineSearch(finalTerm, originalTerm, isCorrected, form);
  },

  handleFormSubmit: function(event, form) {
    if (event) event.preventDefault();

    const targetForm = form || (event ? event.target : null);
    if (!targetForm) return;

    const input = targetForm.querySelector(WYSHINE_SEARCH_CONFIG.selectors.searchInput);
    if (!input) return;

    const originalTerm = input.value.trim();
    if (!originalTerm) return;

    const suggestion = buildWyshineSearchSuggestion(originalTerm, { skipCache: true });

    if (
      WYSHINE_SEARCH_CONFIG.autoCorrectOnSubmit &&
      suggestion &&
      suggestion.shouldAutoCorrect &&
      suggestion.corrected
    ) {
      input.value = suggestion.corrected;
      this.submitSearchForm(targetForm, suggestion.corrected, originalTerm, true);
      return;
    }

    this.submitSearchForm(targetForm, originalTerm, '', false);
  },

  expandSearch: function(inputElement) {
    const header = document.querySelector(WYSHINE_SEARCH_CONFIG.selectors.mobileHeader);
    if (header) header.classList.add('search-focused');

    const wrapper = document.querySelector(WYSHINE_SEARCH_CONFIG.selectors.headerWrapper);
    if (wrapper) wrapper.classList.add('wyshine-search-active');

    const predictiveSearch = inputElement.closest(WYSHINE_SEARCH_CONFIG.selectors.predictiveSearch);
    if (predictiveSearch) {
      predictiveSearch.classList.add('wyshine-force-open');
      predictiveSearch.setAttribute('open', 'true');
      this.renderRecentSearches();
      this.toggleClearBtn(inputElement);
      if (this.requestSuggestionUpdate) this.requestSuggestionUpdate(inputElement);
    }
  },

  collapseSearch: function() {
    const header = document.querySelector(WYSHINE_SEARCH_CONFIG.selectors.mobileHeader);
    if (header) header.classList.remove('search-focused');

    const wrapper = document.querySelector(WYSHINE_SEARCH_CONFIG.selectors.headerWrapper);
    if (wrapper) wrapper.classList.remove('wyshine-search-active');

    document.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.predictiveSearch).forEach(predictiveSearch => {
      predictiveSearch.classList.remove('wyshine-force-open');
      predictiveSearch.removeAttribute('open');
    });
  },

  toggleClearBtn: function(input) {
    const wrapper = input.closest(WYSHINE_SEARCH_CONFIG.selectors.predictiveSearch);
    if (!wrapper) return;

    const clearBtn = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.clearButton);
    const zeroState = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.zeroState);
    const searchResults = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.searchResults);
    const suggestionBox = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.suggestionBox);

    if (input.value.trim().length > 0) {
      if (clearBtn) clearBtn.classList.remove('hidden');
      if (zeroState) zeroState.classList.add('hidden');
      if (searchResults) searchResults.classList.remove('hidden');
    } else {
      if (clearBtn) clearBtn.classList.add('hidden');
      if (zeroState) zeroState.classList.remove('hidden');
      if (searchResults) searchResults.classList.add('hidden');
      if (suggestionBox) suggestionBox.style.display = 'none';
      this.renderRecentSearches();
    }
  },

  clearSearch: function(btn) {
    const parent = btn.closest(WYSHINE_SEARCH_CONFIG.selectors.predictiveSearch) || btn.parentElement;
    const input = parent ? parent.querySelector(WYSHINE_SEARCH_CONFIG.selectors.searchInput) : null;

    if (!input) return;

    input.value = '';
    input.focus();
    this.toggleClearBtn(input);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  },

  saveRecentSearch: function(term) {
    const safeTerm = String(term || '').trim();
    if (!safeTerm) return;

    try {
      let recent = safeParseRecentSearches();
      recent = recent.filter(item => item.toLowerCase() !== safeTerm.toLowerCase());
      recent.unshift(safeTerm);
      recent = recent.slice(0, WYSHINE_SEARCH_CONFIG.maxRecentSearches);
      localStorage.setItem(WYSHINE_SEARCH_CONFIG.recentStorageKey, JSON.stringify(recent));
    } catch (error) {}
  },

  renderRecentSearches: function() {
    const recent = safeParseRecentSearches();

    document.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.recentBlock).forEach(block => {
      const listContainer = block.querySelector(WYSHINE_SEARCH_CONFIG.selectors.recentList);
      if (!listContainer) return;

      listContainer.replaceChildren();

      if (!recent.length) {
        block.style.display = 'none';
        return;
      }

      block.style.display = 'block';

      recent.forEach(term => {
        const item = document.createElement('div');
        item.className = 'wyshine-recent-item';

        const link = document.createElement('a');
        link.href = `/search?q=${encodeURIComponent(term)}`;
        link.addEventListener('pointerdown', event => {
          event.preventDefault();
          this.saveRecentSearch(term);
          window.location.href = link.href;
        });

        const text = document.createElement('span');
        text.textContent = term;

        link.append(createSvgIcon('search'), text);

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.setAttribute('aria-label', 'Remove');
        removeButton.addEventListener('pointerdown', event => {
          event.preventDefault();
          this.removeRecentSearch(term);
        });
        removeButton.appendChild(createSvgIcon('x'));

        item.append(link, removeButton);
        listContainer.appendChild(item);
      });
    });
  },

  removeRecentSearch: function(termToRemove) {
    const target = String(termToRemove || '');
    const recent = safeParseRecentSearches().filter(item => item !== target);
    localStorage.setItem(WYSHINE_SEARCH_CONFIG.recentStorageKey, JSON.stringify(recent));
    this.renderRecentSearches();
  },

  clearAllRecent: function() {
    localStorage.removeItem(WYSHINE_SEARCH_CONFIG.recentStorageKey);
    this.renderRecentSearches();
  },

  updateSuggestionBox: function(input) {
    const wrapper = input.closest(WYSHINE_SEARCH_CONFIG.selectors.predictiveSearch);
    if (!wrapper) return;

    const suggestionBox = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.suggestionBox);
    const suggestionLink = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.suggestionLink);
    if (!suggestionBox || !suggestionLink) return;

    const suggestion = buildWyshineSearchSuggestion(input.value);

    if (!suggestion || !suggestion.best) {
      suggestionBox.style.display = 'none';
      suggestionLink.textContent = '';
      suggestionLink.onclick = null;
      suggestionLink.removeAttribute('href');
      return;
    }

    suggestionBox.style.display = 'block';
    suggestionLink.textContent = suggestion.best;
    suggestionLink.setAttribute('href', `/search?q=${encodeURIComponent(suggestion.best)}`);

    suggestionLink.onclick = event => {
      event.preventDefault();
      input.value = suggestion.best;
      const form = input.closest('form');
      if (form) {
        this.submitSearchForm(form, suggestion.best, '', false);
      } else {
        this.saveRecentSearch(suggestion.best);
        navigateToWyshineSearch(suggestion.best);
      }
    };
  },

  initSmartObserver: function() {
    const searchResultsBoxes = document.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.searchResults);

    searchResultsBoxes.forEach(resultsBox => {
      if (resultsBox.dataset.wyshineObserved === 'true') return;
      resultsBox.dataset.wyshineObserved = 'true';

      const observer = new MutationObserver(() => {
        const wrapper = resultsBox.closest(WYSHINE_SEARCH_CONFIG.selectors.predictiveSearch);
        if (!wrapper) return;

        const zeroState = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.zeroState);
        const input = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.searchInput);
        const suggestionBox = wrapper.querySelector(WYSHINE_SEARCH_CONFIG.selectors.suggestionBox);

        if (!input || input.value.trim().length === 0) return;

        const realItems = resultsBox.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.realResultItem);
        const hasItem = realItems.length > 0;
        const hasResponse = resultsBox.textContent.trim().length > 0 || resultsBox.children.length > 0;

        if (!hasItem && hasResponse) {
          resultsBox.classList.add('hidden');
          if (zeroState) zeroState.classList.remove('hidden');
          if (this.requestSuggestionUpdate) this.requestSuggestionUpdate(input);
        } else if (hasItem) {
          resultsBox.classList.remove('hidden');
          if (zeroState) zeroState.classList.add('hidden');
          if (suggestionBox) suggestionBox.style.display = 'none';
        }
      });

      observer.observe(resultsBox, { childList: true, subtree: true });
    });
  },

  bindSearchForms: function() {
    document.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.searchInput).forEach(input => {
      const form = input.closest('form');
      if (!form || form.dataset.wyshineSubmitBound === 'true') return;

      const inlineSubmit = form.getAttribute('onsubmit') || '';
      if (inlineSubmit.includes('WyshineSearch.handleFormSubmit')) return;

      form.dataset.wyshineSubmitBound = 'true';
      form.addEventListener('submit', event => this.handleFormSubmit(event, form));
    });
  },

  bindInputs: function() {
    document.querySelectorAll(WYSHINE_SEARCH_CONFIG.selectors.searchInputsToFocus).forEach(input => {
      if (input.dataset.wyshineInputBound === 'true') return;
      input.dataset.wyshineInputBound = 'true';

      input.addEventListener('touchstart', function() { this.focus(); }, { passive: true });
      input.addEventListener('click', function() { this.focus(); });
      input.addEventListener('focus', () => this.expandSearch(input));
      input.addEventListener('input', () => {
        this.toggleClearBtn(input);
        if (this.requestSuggestionUpdate) this.requestSuggestionUpdate(input);
      });
    });
  },

  init: function() {
    this.requestSuggestionUpdate = debounceWyshine(input => {
      this.updateSuggestionBox(input);
    }, WYSHINE_SEARCH_CONFIG.debounceDelay);

    this.syncTrendingKeywords();
    this.bindInputs();
    this.bindSearchForms();
    this.initSmartObserver();
    injectWyshineCorrectionNote();

    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');
    if (searchQuery) this.saveRecentSearch(searchQuery);

    this.renderRecentSearches();
  }
};

window.WyshineSearch = WyshineSearch;

document.addEventListener('DOMContentLoaded', function() {
  WyshineSearch.init();

  document.addEventListener('click', function(event) {
    const link = event.target.closest('a');
    if (link && link.href && link.href.includes('q=')) {
      try {
        const url = new URL(link.href, window.location.origin);
        const query = url.searchParams.get('q');
        if (query) {
          WyshineSearch.saveRecentSearch(query);
          WyshineSearch.renderRecentSearches();
        }
      } catch (error) {}
    }

    const isClickInsideSearch = event.target.closest(WYSHINE_SEARCH_CONFIG.selectors.predictiveWrapper);
    const isClickCancelBtn = event.target.closest(WYSHINE_SEARCH_CONFIG.selectors.searchCancelButton);
    const isClickRecentDelete = event.target.closest(`${WYSHINE_SEARCH_CONFIG.selectors.recentBlock} button`);
    const isClickClearAll = event.target.closest(WYSHINE_SEARCH_CONFIG.selectors.clearAllRecent);
    const isClickSuggestion = event.target.closest(WYSHINE_SEARCH_CONFIG.selectors.suggestionLink);

    if (!isClickInsideSearch && !isClickCancelBtn && !isClickRecentDelete && !isClickClearAll && !isClickSuggestion) {
      WyshineSearch.collapseSearch();
    }
  });
});
